import { ColorOptions, MaterialTextInput, Text, TextInputNew, TextInput } from '@jfs/ui-new'
import { useEffect, useState } from 'react'
import { CvvIcon } from '../../assets/images/svg/Cvv'
import { InvalidCvvIcon } from '../../assets/images/svg/InvalidCvv'
import { convertHexToRGBA } from '../../utils'

interface CVVInputProps {
  value?: string
  innerRef?: any
  prevRef?: any
  nextRef?: any
  isTextInput?: boolean
  maxCvvLength: number
  isValidCVV: (value: boolean) => void
  cvv: string
  setCvv: (value: string) => void
  validCvv?: boolean
  autoFocus?: boolean
  theme?: any
  type?: 'default' | 'side_labels'
}

const CVVInput = ({
  innerRef,
  prevRef,
  nextRef,
  maxCvvLength,
  isValidCVV,
  cvv,
  setCvv,
  isTextInput = false,
  autoFocus = false,
  theme,
  type = 'default',
}: CVVInputProps) => {
  const [errorMsgCvv, setErrorMsgCvv] = useState('')
  const internalOnChangeCvv = (text: string) => {
    errorMsgCvv && setErrorMsgCvv('')
    const numberWithoutSpace = text.replace(/[^0-9]/g, '')
    if (numberWithoutSpace.length !== maxCvvLength) {
      isValidCVV(false)
    }
    setCvv(numberWithoutSpace)
  }

  // Check if the CVV length is equal to the maxCvvLength
  useEffect(() => {
    if (cvv.length === maxCvvLength) {
      nextRef?.current?.focus()
    }
  }, [cvv.length, maxCvvLength, nextRef])

  const internalOnCvvPress = ({ nativeEvent }: { nativeEvent: any }) => {
    if (nativeEvent?.key === 'Backspace' && !cvv.length) {
      prevRef?.current?.focus()
    }
  }

  const onInternalCvvBlur = () => {
    try {
      if (cvv.length < maxCvvLength && cvv.length > 0) {
        throw new Error()
      }
      isValidCVV(true)
    } catch (error) {
      setErrorMsgCvv('Invalid CVV')
      isValidCVV(false)
    }
  }

  const renderIcon = () => {
    if (type === 'side_labels') {
      return null
    }
    if (errorMsgCvv) {
      return <InvalidCvvIcon width={30} height={30} />
    } else {
      return <CvvIcon width={30} height={30} color={theme?.brandColor as ColorOptions} />
    }
  }

  const leftComp = () => {
    if (type === 'default') {
      return null
    }
    return (
      <Text
        title={'CVV'}
        fontFamily={'poppins-regular'}
        fontSize={10}
        color={convertHexToRGBA(theme?.bodyTextColor, 40) as ColorOptions}
      />
    )
  }

  if (type === 'side_labels') {
    return (
      <TextInputNew
        autoFocus={autoFocus}
        innerRef={innerRef}
        onKeyPress={internalOnCvvPress}
        maxLength={maxCvvLength}
        value={cvv}
        secureTextEntry={true}
        onChangeText={(text: string) => internalOnChangeCvv(text)}
        onBlur={onInternalCvvBlur}
        keyboardType={'number-pad'}
        error={errorMsgCvv}
        touched={true}
        testID={'cvvTextInput'}
        rightComponent={renderIcon()}
        color={theme?.bodyTextColor as ColorOptions}
        textAlign={'right'}
        borderColor={convertHexToRGBA(theme?.brandColor, 40) as ColorOptions}
        focusBorderColor={convertHexToRGBA(theme?.brandColor, 40) as ColorOptions}
        borderRadius={8}
        paddingHorizontal={8}
        paddingVertical={14}
        type={'body12'}
        showBottomLine={false}
        leftComponent={leftComp()}
      />
    )
  }

  if (isTextInput) {
    return (
      <TextInput
        autoFocus={autoFocus}
        innerRef={innerRef}
        isAutoFocus={true}
        onKeyPress={internalOnCvvPress}
        maxLength={maxCvvLength}
        value={cvv}
        label="CVV"
        showContainerPadding={false}
        secureTextEntry={true}
        onChangeText={(text: string) => internalOnChangeCvv(text)}
        onBlur={onInternalCvvBlur}
        keyboardType={'number-pad'}
        error={errorMsgCvv}
        testID={'cvvTextInput'}
        rightComponent={renderIcon()}
        bottomLineColor={theme?.brandColor as ColorOptions}
        color={theme?.bodyTextColor as ColorOptions}
        selectionColor={theme?.brandColor as ColorOptions}
        placeholderTextColor={theme?.brandColor as ColorOptions}
        onFocusBottomLineColor={theme?.brandColor as ColorOptions}
        fontFamily={'poppins-medium'}
        fontSize={12}
      />
    )
  }

  return (
    <MaterialTextInput
      autoFocus={autoFocus}
      innerRef={innerRef}
      onKeyPress={internalOnCvvPress}
      maxLength={maxCvvLength}
      value={cvv}
      secureTextEntry={true}
      onChangeText={(text: string) => internalOnChangeCvv(text)}
      placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
      onBlur={onInternalCvvBlur}
      color={theme?.bodyTextColor}
      placeholder={'CVV'}
      keyboardType={'number-pad'}
      error={errorMsgCvv}
      testID={'cvvTextInput'}
      rightComponent={renderIcon()}
      bottomLineColor={theme?.brandColor as ColorOptions}
      tintColor={theme?.brandColor as ColorOptions}
    />
  )
}

export { CVVInput }
